import { useEffect, useRef, useState } from 'react';
import { ArrowRight, Send } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Avatar } from '../components/ui';
import { billsAPI } from '../lib/api';
import { useCitizenEra, useConcludedYear } from '../domain/useCitizenEra';
import { useProfile } from '../hooks/useCitizenData';
import paulaAvatar from '../assets/paula.png';

interface BillHit {
  id: string;
  number: string;
  title: string;
  /** Set when the bill is not from the current session. */
  concludedYear: number | null;
}

interface Message {
  from: 'assistant' | 'user';
  text?: string;
  bills?: BillHit[];
  suggestions?: string[];
}

// Paula is a guided helper: she greets, offers topic suggestions from the
// citizen's priorities, and answers free-text questions by searching real
// legislation via /bills. A full conversational AI backend is a future
// upgrade — this keeps the chat genuinely useful in the meantime.
export default function ChatScreen() {
  const eraScope = useCitizenEra();
  const concludedYear = useConcludedYear();
  const navigate = useNavigate();
  const { data: profile } = useProfile();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Seed the greeting once the profile (and its priorities) is available
  useEffect(() => {
    if (messages.length > 0) return;
    const interests = profile?.interests || [];
    const suggestions = interests.length > 0 ? interests.slice(0, 4) : ['Housing', 'Education', 'Healthcare', 'Taxes'];
    setMessages([
      {
        from: 'assistant',
        text: "Hi — I'm Paula, your Capitol Commons guide. Ask me about any issue and I'll find the bills moving on it right now.",
      },
      {
        from: 'assistant',
        text: interests.length > 0 ? 'Want to start with something you care about?' : 'Pick a topic, or type your own:',
        suggestions,
      },
    ]);
  }, [profile, messages.length]);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, typing]);

  async function ask(query: string) {
    const q = query.trim();
    if (!q) return;
    setMessages((m) => [...m, { from: 'user', text: q }]);
    setInput('');
    setTyping(true);
    try {
      /*
        Scoped to the eras Citizen may address (PRD §5.D3). Unscoped, this
        returned bills of any age with nothing marking them dead — on the app's
        highest-trust surface.
      */
      const { data } = await billsAPI.getAll({
        search: q,
        limit: 4,
        session: eraScope.allowedSessionKeys.join(',') || 'current',
      });
      const rows = data.data?.bills || data.data || [];
      const bills: BillHit[] = (Array.isArray(rows) ? rows : []).slice(0, 4).map((b: any) => ({
        id: b.id,
        number: b.number || b.bill_number,
        title: b.title,
        concludedYear: concludedYear(b.session_key),
      }));
      setTyping(false);
      if (bills.length > 0) {
        setMessages((m) => [
          ...m,
          {
            from: 'assistant',
            text: `Here's what's moving on "${q}" right now:`,
            bills,
          },
        ]);
      } else {
        setMessages((m) => [
          ...m,
          {
            from: 'assistant',
            text: `I couldn't find active bills matching "${q}" yet. Try another topic, or browse your ballot for what's coming up.`,
          },
        ]);
      }
    } catch {
      setTyping(false);
      setMessages((m) => [
        ...m,
        { from: 'assistant', text: 'Something went wrong searching just now. Mind trying again?' },
      ]);
    }
  }

  return (
    <div className="flex flex-col h-screen screen-enter">
      {/* Header */}
      <div
        className="app-header flex items-center justify-between px-4 border-b sticky top-0 z-30 backdrop-blur"
        style={{ background: 'color-mix(in oklab, var(--background) 92%, transparent)', borderColor: 'var(--border)' }}
      >
        <div className="flex items-center gap-3">
          <Avatar initials="P" size={36} tone="dark" src={paulaAvatar} />
          <div className="flex flex-col">
            <span className="font-bold text-base leading-tight">Paula</span>
            <span className="text-[10px] text-[color:var(--muted-foreground)] flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
              Online
            </span>
          </div>
        </div>
      </div>

      {/* Conversation */}
      <div className="flex-1 overflow-y-auto px-4 py-5" style={{ paddingBottom: '96px' }}>
        <div className="text-center mb-4">
          <span className="text-xs text-[color:var(--muted-foreground)] bg-[var(--secondary)] px-3 py-1 rounded-full">
            Today, {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>

        <div className="space-y-4">
          {messages.map((m, i) => {
            if (m.from === 'user') {
              return (
                <div key={i} className="flex justify-end anim-slide">
                  <div
                    className="max-w-[80%] rounded-2xl rounded-br-sm px-4 py-2.5 text-sm leading-relaxed"
                    style={{ background: 'var(--primary)', color: 'white' }}
                  >
                    {m.text}
                  </div>
                </div>
              );
            }
            return (
              <div key={i} className="space-y-2">
                <div className="flex items-end gap-2 anim-slide">
                  <Avatar initials="P" size={32} tone="dark" src={paulaAvatar} />
                  <div
                    className="max-w-[80%] rounded-2xl rounded-bl-sm px-4 py-3 text-sm leading-relaxed border whitespace-pre-line"
                    style={{ background: 'var(--card)', borderColor: 'var(--border)' }}
                  >
                    {m.text}
                  </div>
                </div>

                {m.bills && (
                  <div className="ml-12 space-y-2 anim-slide">
                    {m.bills.map((b) => (
                      <button
                        key={b.id}
                        onClick={() => navigate(`/citizen/bill/${b.id}`)}
                        className="w-full text-left rounded-xl border p-3 flex items-center gap-3 hover-elevate active-elevate-2"
                        style={{ background: 'var(--card)', borderColor: 'var(--border)' }}
                      >
                        <span
                          className="text-[11px] font-bold px-2 py-0.5 rounded-md shrink-0"
                          style={{ background: 'var(--primary)', color: 'white' }}
                        >
                          {b.number}
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="text-sm font-semibold line-clamp-2 leading-tight block">{b.title}</span>
                          {b.concludedYear !== null && (
                            <span className="mt-1 inline-block text-[11px] font-semibold px-1.5 py-0.5 rounded bg-[color:var(--muted)] text-[color:var(--muted-foreground)]">
                              Concluded · {b.concludedYear} — finished last session, not moving
                            </span>
                          )}
                        </span>
                        <ArrowRight className="w-4 h-4 text-[color:var(--muted-foreground)] shrink-0" />
                      </button>
                    ))}
                  </div>
                )}

                {m.suggestions && (
                  <div className="ml-12 flex flex-wrap gap-2 anim-slide">
                    {m.suggestions.map((s) => (
                      <button
                        key={s}
                        onClick={() => ask(s)}
                        className="rounded-full px-3 py-1.5 text-xs font-semibold border hover-elevate"
                        style={{ background: 'var(--card)', color: 'var(--primary)', borderColor: 'var(--accent)' }}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {typing && (
            <div className="flex items-end gap-2 anim-slide">
              <Avatar initials="P" size={32} tone="dark" src={paulaAvatar} />
              <div
                className="rounded-2xl rounded-bl-sm px-4 py-3 border flex gap-1.5"
                style={{ background: 'var(--card)', borderColor: 'var(--border)' }}
              >
                {[0, 120, 240].map((d) => (
                  <span
                    key={d}
                    className="w-1.5 h-1.5 rounded-full bg-[color:var(--muted-foreground)]"
                    style={{ animation: `pulse-ping 1.4s ${d}ms infinite` }}
                  />
                ))}
              </div>
            </div>
          )}
          <div ref={scrollRef} />
        </div>
      </div>

      {/* Input */}
      <div
        className="fixed left-0 right-0 max-w-md mx-auto p-4 border-t"
        style={{ bottom: 'calc(56px + max(env(safe-area-inset-bottom), 20px))', borderColor: 'var(--border)', background: 'var(--background)' }}
      >
        <div className="flex items-center gap-2 rounded-3xl p-1.5" style={{ background: 'var(--secondary)' }}>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') ask(input);
            }}
            placeholder="Ask about an issue or topic…"
            className="flex-1 bg-transparent border-0 focus:outline-none text-sm py-2 px-3"
          />
          <button
            onClick={() => ask(input)}
            disabled={!input.trim()}
            className="rounded-full shrink-0 h-9 w-9 flex items-center justify-center disabled:opacity-40"
            style={{ background: 'var(--primary)', color: 'white' }}
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
